import { ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PersonType } from '@/../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Person } from '@/api/types/backend-dto-types';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/new-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { TextWithReferences } from '@/helpers/formatReferences';

interface PersonRowProps {
  person: Person;
}

const getPersonTypeDisplay = (type: PersonType) => {
  return capitalizeFirstLetter(type);
};

const getPersonTypeBadgeProps = (type: PersonType) => {
  switch (type) {
    case PersonType.Person:
      return {
        variant: 'default' as const,
        className: 'text-xs bg-blue-100 text-blue-800 border-blue-200',
      };
    case PersonType.Organisation:
      return {
        variant: 'secondary' as const,
        className: 'text-xs bg-purple-100 text-purple-800 border-purple-200',
      };
    case PersonType.Place:
      return {
        variant: 'secondary' as const,
        className: 'text-xs bg-teal-100 text-teal-700 border-teal-200',
      };
    default:
      return {
        variant: 'outline' as const,
        className: 'text-xs',
      };
  }
};

export const PersonRow = ({ person }: PersonRowProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleExploreFacts = (personId: string) => {
    trackCustomEvent(TrackingEvents.FilterFactsByPerson);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('personId', personId);
    navigate(`/app/chronos/case-editor/data/facts?${newSearchParams.toString()}`);
  };

  return (
    <div className="flex flex-col rounded-lg border bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      {/* Header Section */}
      <div className="mb-3 flex items-center justify-between border-b pb-3">
        <div className="flex-1 font-semibold">
          <h3 className="text-lg font-semibold text-gray-900">{person.name}</h3>
        </div>
        <div className="ml-4 flex items-center gap-2">
          <Badge {...getPersonTypeBadgeProps(person.type)}>{getPersonTypeDisplay(person.type)}</Badge>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-4">
        {/* Aliases Section */}
        {person.aliases && person.aliases.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-gray-600">Aliases:</span>
            <div className="flex min-w-0 items-center gap-1">
              <span className="truncate text-gray-700">{person.aliases.slice(0, 4).join(', ')}</span>
              {person.aliases.length > 4 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-700 hover:underline">
                      +{person.aliases.length - 4} more
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3">
                    <div className="text-sm text-gray-700">
                      <div className="mb-1 font-medium">All aliases:</div>
                      <div className="text-xs">{person.aliases.slice(4).join(', ')}</div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        )}

        {/* Description Section */}
        {person.description && (
          <div className="text-sm text-gray-600">
            <TextWithReferences content={person.description} referencesDict={person.references ?? undefined} />
          </div>
        )}

        {/* Footer Section */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => handleExploreFacts(person.id)}
              size="sm"
              variant="outline"
              className="flex items-center gap-2"
            >
              <span>Related Facts</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
